<template>
  <div class="ApplicationList grey lighten-6 mx-12">
    <!-- List view filters and actions -->
    <v-row class="pb-3 mb-4" no-gutters>
      <v-col class="pt-0" cols="12" md="9">
        <span class="filterLabel">Filter by:</span>
        <span class="pl-3">
          <YearDateRangeFilter
            ref="receivedDateFilter"
            label="Received date"
            :filter-options="{ disabled: !hasData }"
            :year-options="receivedYearOptions"
            :show-year-filter="true"
            :selected-application="STREAM.REGISTERED_INTEREST"
            @updatefilter="updateDateFilter"
            @updateYearFilter="updateReceivedYearFilter"
            @menuOpened="sendFilterAnalytics"
          />
        </span>
        <span class="pl-3">
          <ListViewFilter
            ref="scholasticYearFilter"
            label="Scholastic year"
            type="checkbox"
            :filter-options="scholasticYearFilterOptions"
            @updatefilter="updateScholasticYearFilter"
            @menuOpened="sendFilterAnalytics"
          />
        </span>
        <span class="pl-3">
          <ListViewFilter
            ref="startYearFilter"
            label="Start year"
            type="checkbox"
            :filter-options="startYearFilterOptions"
            :show-default-filter="startYearFilters"
            @updatefilter="updateStartYearFilter"
            @menuOpened="sendFilterAnalytics"
          />
        </span>
        <span class="pl-3">
          <ListViewFilter
            ref="applicationTypeFilter"
            label="Type"
            type="checkbox"
            :filter-options="applicationTypeFilterOptions"
            @updatefilter="updateApplicationTypeFilter"
            @menuOpened="sendFilterAnalytics"
          />
        </span>
        <span class="pl-3">
          <ListViewFilter
            ref="emailSentFilter"
            label="Email sent"
            type="checkbox"
            :filter-options="emailSentFilterOptions"
            @updatefilter="updateEmailSentFilter"
            @menuOpened="sendFilterAnalytics"
          />
        </span>
        <ClearFilters @click="clearAllFilters()" v-if="isFiltered" />
      </v-col>
      <v-col class="pt-0 d-flex justify-end gap-2" cols="12" md="3">
        <AdsButton
          @click="sendEmail"
          button-text="Send enrolment email"
          :disabled="emailDisabled"
          icon="mdi-email-outline"
        />

        <AdsButton
          @click="exportCSV"
          :disabled="csvDisabled"
          icon="mdi-export"
          button-text="Export CSV"
        />
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-card elevation="2" class="mb-20">
      <AdsDataTable
        :items="filteredRegisteredInterests"
        :headers="headers"
        search-label="Find student"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        must-sort
        :items-per-page="itemsPerPage"
        :footer-props="{
          'items-per-page-options': [10, 15, 50]
        }"
        :custom-filter="customFilter"
        :search="effectiveSearchQuery"
        @input="updateSearchQuery"
        @update:items-per-page="getItemPerPage"
        @update:search="updateSearchQuery"
        @update:sort-by="updateSortBy"
        @update:sort-desc="updateSortDesc"
      >
        <template #no-data>
          <NoResultsFound
            :filtered="isFiltered || !!searchQuery"
            :has-archive-link="false"
          />
        </template>

        <template #top>
          <v-row class="row-line no-gutters mb-0 px-7">
            <v-col>
              <v-text-field
                class="searchInput"
                prepend-inner-icon="mdi-magnify"
                v-model="searchQuery"
                placeholder="Search for a record"
                single-line
                hide-details
                clearable
              />
            </v-col>
            <v-col
              cols="auto"
              class="results-text d-flex align-center justify-center"
            >
              <span class="mt-1 pt-3">{{
                resultsText(displayedRegisteredInterests.length)
              }}</span>
            </v-col>
          </v-row>
        </template>

        <template
          v-for="headerData in headers"
          :slot="`header.${headerData.value}`"
        >
          <button class="no-styling" :key="headerData.value">
            {{ headerData.text }}
          </button>
        </template>

        <template #item="props">
          <tr class="grey--text text--lighten-1">
            <td class="checkbox-cell">
              <v-checkbox
                :input-value="selectedRows.includes(props.item.id)"
                :disabled="isCheckboxDisabled(props.item)"
                @change="toggleRowSelection(props.item.id)"
              />
            </td>
            <td class="text-xs-right">
              {{ formatDate(props.item.received) }}
            </td>

            <td class="text-xs-right">
              {{ props.item.studentName }}
            </td>

            <td class="text-left">
              <v-avatar size="30" class="mr-2" color="blue lighten-2">
                <strong class="white--text">{{
                  props.item.scholasticYear
                }}</strong>
              </v-avatar>
              <span class="d-none d-lg-inline">
                {{ formatScholasticYear(props.item.scholasticYear) }}
              </span>
            </td>

            <td class="text-xs-right">
              {{ props.item.startYear }}
            </td>

            <td class="text-xs-right">
              {{ props.item.type }}
            </td>

            <td class="text-xs-right">
              {{ props.item.contactName }}
            </td>

            <td class="text-xs-right">
              {{ props.item.contactEmail }}
            </td>

            <td class="text-left">
              {{ props.item.emailSent ? 'Yes' : 'No' }}
            </td>

            <td class="text-left">
              {{ props.item.notes || '' }}
              <p>
                <AdsButton
                  v-if="props.item.notes"
                  tertiary
                  size="x-small"
                  color="primary"
                  class="cursor-pointer ma-0 pa-0 note-button"
                  @click="openEditNotesModal(props.item)"
                  aria-label="Edit note for registration of interest"
                  icon="mdi-playlist-edit"
                  button-text="Edit note"
                  height="32px"
                />

                <AdsButton
                  v-else
                  tertiary
                  size="x-small"
                  color="primary"
                  class="cursor-pointer ma-0 pa-0 note-button"
                  @click="openEditNotesModal(props.item)"
                  aria-label="Add note for registration of interest"
                  icon="mdi-plus-box-outline"
                  button-text="Add note"
                  height="32px"
                />
              </p>
            </td>

            <td class="text-center">
              <v-icon
                color="error"
                class="cursor-pointer"
                @click="openDeleteConfirmModal(props.item)"
                aria-label="Delete registration of interest"
              >
                mdi-delete-outline
              </v-icon>
            </td>
          </tr>
        </template>
      </AdsDataTable>
    </v-card>

    <!-- Notes Modal -->
    <AppModal
      v-if="showNotesModal"
      @close="closeNotesModal"
      max-width="662px"
      data-testid="notes-modal"
    >
      <template slot="header">
        <div class="d-flex justify-space-between pt-3 pl-3 w-full">
          <span class="font-weight-bold primary--text" role="alert">
            <v-avatar size="40" class="mr-2" color="blue lighten-4">
              <v-icon color="primary">
                {{
                  isEditingNote ? 'mdi-playlist-edit' : 'mdi-plus-box-outline'
                }}</v-icon
              >
            </v-avatar>
            {{ isEditingNote ? 'Edit note' : 'Add note' }}</span
          >
        </div>
      </template>

      <template slot="body">
        <div class="content mt-3 mx-3 my-3">
          <v-textarea
            label="Note details"
            v-model="notesText"
            color="primary"
            outlined
            dense
            persistent-placeholder
            rows="6"
            counter="250"
            maxlength="250"
            class="mt-2"
          ></v-textarea>
        </div>
      </template>

      <template #footer>
        <div class="mb-0 mr-3 pb-2 d-flex justify-space-between">
          <div>
            <AdsButton
              tertiary
              icon="mdi-close"
              aria-label="close"
              button-text="Cancel"
              @click="closeNotesModal"
              data-testid="btn-close-notes-modal"
            />
            <AdsButton
              icon="mdi-check"
              button-text="Save changes"
              aria-label="Save changes"
              :disabled="!notesText.trim()"
              @click="saveNotes"
            />
          </div>

          <AdsButton
            v-if="isEditingNote"
            color="error"
            icon="mdi-delete-outline"
            button-text="Delete note"
            aria-label="Delete note"
            @click="deleteNotes"
          />
        </div>
      </template>
    </AppModal>

    <!-- Delete Note Confirmation Modal -->
    <AppModal
      v-if="showDeleteConfirmModal"
      v-model="showDeleteConfirmModal"
      ref="DeleteConfirmModal"
      data-testid="delete-note-confirm-modal"
      max-width="525px"
      role-type="none"
    >
      <template slot="header">
        <div
          class="d-flex justify-space-between pt-3 pl-3 w-full primary--text"
        >
          <span class="font-weight-bold" role="alert">
            <v-avatar size="40" class="mr-2" color="blue lighten-4">
              <v-icon color="primary"> mdi-delete-outline</v-icon>
            </v-avatar>
            Delete note</span
          >
        </div>
      </template>
      <template slot="body">
        <div class="content mt-3 mx-3 my-3">
          <p class="mb-2 text--primary" role="alert">
            Are you sure you want to delete this note for
            <strong>{{ studentNameForDelete }}</strong
            >? This action cannot be undone.
          </p>
        </div>
      </template>
      <template #footer>
        <div class="mb-0 mr-3 pb-2 d-flex justify-space-between">
          <AdsButton
            color="error"
            icon="mdi-check"
            button-text="Confirm deletion"
            @click="confirmDelete"
            data-testid="confirm-delete-note"
          />
          <AdsButton
            button-text="Cancel"
            :secondary="true"
            icon="mdi-close"
            @click="cancelDelete"
            data-testid="cancel-delete-note"
          />
        </div>
      </template>
    </AppModal>

    <!-- Send Enrolment Email Confirmation Modal -->
    <AppModal
      v-if="showSendEmailConfirmModal"
      v-model="showSendEmailConfirmModal"
      ref="SendEmailConfirmModal"
      data-testid="send-email-confirm-modal"
      max-width="525px"
      role-type="none"
    >
      <template slot="header">
        <div
          class="d-flex justify-space-between pt-3 pl-3 w-full primary--text"
        >
          <span class="font-weight-bold" role="alert">
            <v-avatar size="40" class="mr-2" color="blue lighten-4">
              <v-icon color="primary"> mdi-send</v-icon>
            </v-avatar>
            Send enrolment email</span
          >
        </div>
      </template>
      <template slot="body">
        <div class="content mt-3 mx-3 my-3">
          <p class="mb-2 text--primary" role="alert">
            Are you sure you want to send the enrolment email to
            <strong>{{ selectedRows.length }} selected recipient(s)</strong>?
          </p>
        </div>
      </template>
      <template #footer>
        <div class="mb-0 mr-3 pb-2 d-flex justify-space-between">
          <AdsButton
            color="primary"
            icon="mdi-check"
            button-text="Send"
            @click="confirmSendEmail"
            data-testid="confirm-send-email"
          />
          <AdsButton
            button-text="Cancel"
            secondary
            icon="mdi-close"
            @click="cancelSendEmail"
            data-testid="cancel-send-email"
          />
        </div>
      </template>
    </AppModal>

    <!-- Delete Registered Interest Confirmation Modal -->
    <AppModal
      v-if="showDeleteRegistrationConfirmModal"
      data-testid="delete-registration-confirm-modal"
      max-width="525px"
      @close="closeDeleteRegistrationConfirmModal"
    >
      <template slot="header">
        <div
          class="d-flex justify-space-between pt-3 pl-3 w-full primary--text"
        >
          <span class="font-weight-bold" role="alert">
            <v-avatar size="40" class="mr-2" color="blue lighten-4">
              <v-icon color="primary">mdi-delete-outline</v-icon>
            </v-avatar>
            Delete Registered Interest
          </span>
        </div>
      </template>
      <template slot="body">
        <div class="content mt-3 mx-3 my-3">
          <p class="mb-2 text--primary" role="alert">
            Are you sure you want to delete this Registered Interest for
            <strong v-if="registrationToDelete">{{
              registrationToDelete.studentName
            }}</strong
            >? This action cannot be undone.
          </p>
        </div>
      </template>
      <template #footer>
        <div class="mb-0 mr-3 pb-2 d-flex justify-space-between">
          <AdsButton
            color="error"
            icon="mdi-check"
            button-text="Confirm deletion"
            @click="confirmDeleteRegistration"
            data-testid="confirm-delete-registration"
          />
          <AdsButton
            button-text="Cancel"
            secondary
            icon="mdi-close"
            @click="cancelDeleteRegistration"
            data-testid="cancel-delete-registration"
          />
        </div>
      </template>
    </AppModal>
  </div>
</template>

<script>
import ListViewFilter from '@/components/app/ListViewFilter.vue'
import YearDateRangeFilter from '@/components/app/YearDateRangeFilter.vue'
import NoResultsFound from '@/components/app/NoResultsFound.vue'
import ClearFilters from './ClearFilters.vue'
import AppModal from '@/components/app/AppModal.vue'
import { AdsDataTable, AdsButton } from '@nswdoe/doe-ui-core'
import { STREAM, MAX_SELECTION_LIMIT } from '@/constants'

const headerDefinitions = {
  selection: {
    text: '',
    value: 'selection',
    class: 'selection-checkbox',
    align: 'center',
    width: '40px',

    sortable: false
  },
  received: {
    text: 'Received',
    value: 'received',
    class: 'date-received',
    align: 'start',
    width: '100px'
  },
  contactName: {
    text: 'Contact name',
    value: 'contactName',
    class: 'contact-name',
    align: 'start',
    width: '120px'
  },
  contactEmail: {
    text: 'Contact email',
    value: 'contactEmail',
    class: 'contact-email',
    align: 'start',
    width: '160px'
  },
  studentName: {
    text: 'Student name',
    value: 'studentName',
    class: 'student-name',
    align: 'start',
    width: '120px'
  },
  scholasticYear: {
    text: 'Scholastic year',
    value: 'scholasticYear',
    align: 'start',
    class: 'scholastic-year',
    width: '120px'
  },
  startYear: {
    text: 'Start year',
    value: 'startYear',
    class: 'start-year',
    align: 'start',
    width: '100px'
  },
  type: {
    text: 'Type',
    value: 'type',
    class: 'application-type',
    align: 'start',
    width: '90px'
  },

  emailSent: {
    text: 'Email sent',
    value: 'emailSent',
    class: 'email-sent',
    align: 'start',
    width: '100px'
  },
  notes: {
    text: 'Notes',
    value: 'notes',
    class: 'notes',
    align: 'start',
    width: '190px',
    sortable: false
  },
  actions: {
    text: 'Delete contact',
    value: 'actions',
    class: 'actions',
    align: 'center',
    width: '110px',
    sortable: false
  }
}

export default {
  name: 'RegisteredInterestDataTable',
  components: {
    ListViewFilter,
    YearDateRangeFilter,
    NoResultsFound,
    ClearFilters,
    AppModal,
    AdsDataTable,
    AdsButton
  },

  data() {
    return {
      STREAM,
      MAX_SELECTION_LIMIT,
      searchQuery: '',
      sortBy: 'startYear',
      sortDesc: false,
      itemsPerPage: 50,
      dateRangeFilter: [],
      scholasticYearFilters: [],
      startYearFilters: [],
      applicationTypeFilters: [],
      emailSentFilters: [],
      selectedReceivedYear: [],
      headers: [
        headerDefinitions.selection,
        headerDefinitions.received,
        headerDefinitions.studentName,
        headerDefinitions.scholasticYear,
        headerDefinitions.startYear,
        headerDefinitions.type,
        headerDefinitions.contactName,
        headerDefinitions.contactEmail,
        headerDefinitions.emailSent,
        headerDefinitions.notes,
        headerDefinitions.actions
      ],
      selectedRows: [],
      snackbarMessage: '',
      showNotesModal: false,
      showDeleteConfirmModal: false,
      showSendEmailConfirmModal: false,
      showDeleteRegistrationConfirmModal: false,
      notesData: null,
      notesText: '',
      isEditingNote: false,
      studentNameForDelete: '',
      deleteApplicationId: null,
      registrationToDelete: null
    }
  },
  mounted() {
    if (this.schoolCode) {
      this.$store.dispatch('futureEnrolment/fetchRegistrationOfInterest', {
        schoolId: this.schoolCode
      })
    }
  },
  watch: {
    schoolCode() {
      // Fetch fresh data and clear filters on change of school switcher
      this.clearAllFilters()
      this.searchQuery = ''
      if (this.schoolCode) {
        this.$store.dispatch('futureEnrolment/fetchRegistrationOfInterest', {
          schoolId: this.schoolCode
        })
      }
    },
    selectedRows(newVal) {
      // Show snackbar when max selection is reached
      if (newVal.length === this.MAX_SELECTION_LIMIT) {
        this.$store.dispatch('showSnackbar', {
          display: true,
          html: `You can select a maximum of ${this.MAX_SELECTION_LIMIT} records at a time.`,
          icon: {
            name: 'mdi-alert-circle',
            color: '#b81237'
          },
          timeout: 5000,
          closeBtn: true
        })
      }
    }
  },
  computed: {
    filteredRegisteredInterests() {
      if (!this.registeredInterestsList) {
        return []
      }

      return this.registeredInterestsList.filter((item) => {
        // Apply received year filter
        if (this.selectedReceivedYear.length > 0) {
          const itemYear = new Date(item.received).getFullYear().toString()
          if (!this.filterReceivedYear(itemYear)) {
            return false
          }
        }

        // Apply date range filter
        if (this.dateRangeFilter.length === 2) {
          const itemDate = new Date(item.received)
          const startDate = new Date(this.dateRangeFilter[0])
          const endDate = new Date(this.dateRangeFilter[1])

          // Set time to midnight for date-only comparison (local timezone)
          itemDate.setHours(0, 0, 0, 0)
          startDate.setHours(0, 0, 0, 0)
          endDate.setHours(0, 0, 0, 0)

          if (itemDate < startDate || itemDate > endDate) {
            return false
          }
        }

        // Apply scholastic year filter
        if (
          this.scholasticYearFilters.length > 0 &&
          !this.scholasticYearFilters.includes(
            this.formatScholasticYear(item.scholasticYear)
          )
        ) {
          return false
        }

        // Apply start year filter
        if (
          this.startYearFilters.length > 0 &&
          !this.startYearFilters.includes(String(item.startYear))
        ) {
          return false
        }

        // Apply application type filter
        if (
          this.applicationTypeFilters.length > 0 &&
          !this.applicationTypeFilters.includes(item.type)
        ) {
          return false
        }

        // Apply email sent filter
        if (this.emailSentFilters.length > 0) {
          const emailSentStatus = item.emailSent ? 'Yes' : 'No'
          if (!this.emailSentFilters.includes(emailSentStatus)) {
            return false
          }
        }

        return true
      })
    },
    isFiltered() {
      return (
        this.selectedReceivedYear.length > 0 ||
        this.dateRangeFilter.length === 2 ||
        this.scholasticYearFilters.length > 0 ||
        this.startYearFilters.length > 0 ||
        this.applicationTypeFilters.length > 0 ||
        this.emailSentFilters.length > 0
      )
    },
    csvDisabled() {
      return this.displayedRegisteredInterests.length === 0
    },
    emailDisabled() {
      return this.selectedRows.length === 0
    },
    registeredInterestsList() {
      return this.$store.getters['futureEnrolment/registrationOfInterestList']
    },
    hasData() {
      return (
        this.registeredInterestsList && this.registeredInterestsList.length > 0
      )
    },
    effectiveSearchQuery() {
      // Only return search query if 3 or more characters
      return this.searchQuery && this.searchQuery.length >= 3
        ? this.searchQuery
        : ''
    },

    schoolCode() {
      return this.$store.state.selectedSchool?.schoolCode
    },
    scholasticYearFilterOptions() {
      // Extract unique scholastic years from data
      const years = new Set()
      if (this.registeredInterestsList) {
        this.registeredInterestsList.forEach((item) => {
          const formattedYear = this.formatScholasticYear(item.scholasticYear)
          years.add(formattedYear)
        })
      }
      // Sort: Kindergarten first, then Year 1-6
      const sorted = Array.from(years).sort((a, b) => {
        const aNum = a === 'Kindergarten' ? 0 : parseInt(a.replace('Year ', ''))
        const bNum = b === 'Kindergarten' ? 0 : parseInt(b.replace('Year ', ''))
        return aNum - bNum
      })
      return sorted.map((year) => ({ label: year, value: year }))
    },
    startYearFilterOptions() {
      // Extract unique start years from data
      const years = new Set()
      if (this.registeredInterestsList) {
        this.registeredInterestsList.forEach((item) => {
          years.add(String(item.startYear))
        })
      }
      // Sort numerically
      const sorted = Array.from(years).sort((a, b) => parseInt(a) - parseInt(b))
      return sorted.map((year) => ({ label: year, value: year }))
    },
    applicationTypeFilterOptions() {
      // Extract unique application types from data
      const types = new Set()
      if (this.registeredInterestsList) {
        this.registeredInterestsList.forEach((item) => {
          types.add(item.type)
        })
      }
      const sorted = Array.from(types).sort()
      return sorted.map((type) => ({ label: type, value: type }))
    },
    emailSentFilterOptions() {
      // Email sent has two options: Yes or No
      // Disable when there's no data
      if (this.hasData) {
        return [
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' }
        ]
      }
      return []
    },
    receivedYearOptions() {
      // Extract unique years from received dates
      const years = new Set()
      if (this.registeredInterestsList) {
        this.registeredInterestsList.forEach((item) => {
          const year = new Date(item.received).getFullYear().toString()
          years.add(year)
        })
      }
      // Sort numerically in descending order
      const sorted = Array.from(years).sort((a, b) => parseInt(b) - parseInt(a))
      return sorted.map((year) => ({ label: year, value: year }))
    },
    displayedRegisteredInterests() {
      // Apply search filter to filtered results to show accurate count
      if (!this.effectiveSearchQuery) {
        return this.filteredRegisteredInterests
      }
      return this.filteredRegisteredInterests.filter((item) =>
        this.customFilter(null, this.effectiveSearchQuery, item)
      )
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return ''
      return this.$moment(date).format('DD MMM YYYY')
    },
    formatDOB(dob) {
      // Format date of birth of dd-mm-yyyy as DD MMM YYYY, return empty string if not provided
      if (!dob) return ''
      return this.$moment(dob, 'DD-MM-YYYY').format('DD MMM YYYY')
    },
    matchesDateFilter(item) {
      if (this.dateRangeFilter.length !== 2) return true

      const itemDate = new Date(item.received)
      const startDate = new Date(this.dateRangeFilter[0])
      const endDate = new Date(this.dateRangeFilter[1])

      return itemDate >= startDate && itemDate <= endDate
    },
    updateSearchQuery(searchValue) {
      this.searchQuery = searchValue
    },
    updateDateFilter(selectedOptions) {
      this.dateRangeFilter = selectedOptions
    },
    updateScholasticYearFilter(selectedOptions) {
      this.scholasticYearFilters = selectedOptions
    },
    updateStartYearFilter(selectedOptions) {
      this.startYearFilters = selectedOptions
    },
    updateApplicationTypeFilter(selectedOptions) {
      this.applicationTypeFilters = selectedOptions
    },
    updateEmailSentFilter(selectedOptions) {
      this.emailSentFilters = selectedOptions
    },
    updateReceivedYearFilter(selectedYear) {
      if (selectedYear !== null && selectedYear.length > 0) {
        this.selectedReceivedYear = selectedYear
      } else {
        this.selectedReceivedYear = []
      }
    },
    filterReceivedYear(year) {
      return this.selectedReceivedYear.includes(year)
    },
    clearAllFilters() {
      this.$refs.receivedDateFilter?.clearFilter()
      this.$refs.scholasticYearFilter?.clearFilter()
      this.$refs.startYearFilter?.clearFilter()
      this.$refs.applicationTypeFilter?.clearFilter()
      this.$refs.emailSentFilter?.clearFilter()
      this.selectedReceivedYear = []
      this.dateRangeFilter = []
      this.scholasticYearFilters = []
      this.startYearFilters = []
      this.applicationTypeFilters = []
      this.emailSentFilters = []
    },
    customFilter(value, search, item) {
      // Search across all table columns
      if (!search || !item) {
        return true
      }
      const searchTerms = search
        .toLowerCase()
        .split(' ')
        .filter((term) => term)
      const searchableFields = [
        item.studentName || '',
        item.contactName || '',
        item.contactEmail || '',
        this.formatDate(item.received) || '',
        this.formatScholasticYear(item.scholasticYear) || '',
        String(item.startYear) || '',
        item.type || '',
        item.emailSent ? 'Yes' : 'No',
        item.notes || ''
      ]

      return searchTerms.every((term) =>
        searchableFields.some((field) => field.toLowerCase().includes(term))
      )
    },
    getItemPerPage(itemPerPage) {
      this.itemsPerPage = itemPerPage
    },
    sendFilterAnalytics() {
      // Send analytics data when filter is closed
      this.$gtm?.trackEvent?.({
        category: 'applicationFilter',
        action: 'registeredInterest',
        label: 'filtered'
      })
    },
    resultsText(itemsLength) {
      return itemsLength === 1
        ? itemsLength + ' result'
        : itemsLength + ' results'
    },
    sendEmail() {
      if (this.selectedRows.length === 0) {
        return
      }
      // Show confirmation modal
      this.showSendEmailConfirmModal = true
    },
    async confirmSendEmail() {
      const response = await this.$store
        .dispatch('futureEnrolment/sendEmailsForApplications', {
          schoolCode: this.schoolCode,
          applicationIds: this.selectedRows
        })
        .catch(() => {
          this.$store.dispatch('showSnackbar', {
            display: true,
            html: `<b>Unable to complete request</b><br>A connection error has occurred. Please try again later. If this problem persists, please contact our support team.`,
            icon: {
              name: 'mdi-alert-circle',
              color: '#b81237'
            },
            timeout: 5000,
            closeBtn: true
          })
        })

      // Show summary and display snackbar
      if (response.data?.body?.summary) {
        const { total, success, fail } = response.data.body.summary
        this.snackbarMessage = `Emails were sent to ${success} of ${total} parent(s)/carer(s). ${fail} failed to send.`
        this.$store.dispatch('showSnackbar', {
          display: true,
          html: `<b>Email status update</b><br>${this.snackbarMessage}`,
          icon: {
            name: success === total ? 'mdi-check-circle' : 'mdi-alert-circle',
            color: success === total ? '#2f9e44' : '#b81237'
          },
          timeout: 5000,
          closeBtn: true
        })
      }

      // Clear selected rows
      this.selectedRows = []
      // Close confirmation modal
      this.closeSendEmailConfirmModal()
    },
    cancelSendEmail() {
      this.closeSendEmailConfirmModal()
    },
    closeSendEmailConfirmModal() {
      this.showSendEmailConfirmModal = false
      // Clear selected rows
      this.selectedRows = []
    },
    exportCSV() {
      // Generate CSV from filtered data
      const csvContent = this.generateCSV()

      // Create blob and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)

      // Format timestamp as yyyymmddhhmmss
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      const timestamp = `${year}${month}${day}${hours}${minutes}${seconds}`

      link.setAttribute('href', url)
      link.setAttribute('download', `MailingList_${timestamp}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    generateCSV() {
      // update CSV Headers

      const headers = [
        'Reference ID',
        'Received Date',
        'Students name',
        'Date of Birth',
        'Scholastic Year',
        'Intended Start Year',
        'Type',
        'Residential Address',
        'Contact name',
        'Contact email address',
        'Contact number',
        'How did you first hear about us?',
        'Email Sent',
        'Notes'
      ]

      // Apply sorting to displayed results
      const sortedData = this.sortData(this.displayedRegisteredInterests)

      // CSV rows - use sorted and searched results
      const rows = sortedData.map((item) => [
        item.id,
        this.formatDate(item.received),
        item.studentName,
        this.formatDOB(item.studentDOB),
        this.formatScholasticYear(item.scholasticYear),
        item.startYear,
        item.type,
        item.contactAddress,
        item.contactName,
        item.contactEmail,
        this.formatContactNumber(item.contactNumber),
        // Get referral source text, handling "Other" case, if referralSource is "Other" and referralSourceOther has value, use referralSourceOther, else use referralSource value. If referralSource is not provided, return ''
        this.getReferalSourceText(item) || '',
        item.emailSent ? 'Yes' : 'No',
        item.notes || ''
      ])

      // Combine headers and rows
      const csvArray = [headers, ...rows]

      // Convert to CSV string with proper escaping
      return csvArray
        .map((row) =>
          row
            .map((cell) => {
              // Escape quotes and wrap in quotes if contains comma, quotes, or newlines
              const escapedCell = String(cell).replace(/"/g, '""')
              return `"${escapedCell}"`
            })
            .join(',')
        )
        .join('\n')
    },
    getReferalSourceText(item) {
      // Return custom text if referral source is "Other"
      if (item.referralSource === 'OTR' && item.referralSourceOther) {
        return item.referralSourceOther
      }

      // Find and return the referral source value from store properties
      const referralSources =
        this.$store.state.oesProperties?.registrationOfInterest?.referralSources
      const matchedSource = referralSources?.find(
        (source) => source.code === item.referralSource
      )

      return matchedSource?.value ?? ''
    },
    formatContactNumber(phoneNumber) {
      // Return '' if phone number is empty or null
      if (!phoneNumber) {
        return ''
      }

      const phone = `="${phoneNumber}"`

      return phone
    },
    toggleRowSelection(rowId) {
      // Prevent selection of rows where email has been sent
      const item = this.filteredRegisteredInterests.find((i) => i.id === rowId)
      if (item?.emailSent) {
        return
      }

      const index = this.selectedRows.indexOf(rowId)
      // If row is already selected, allow deselection
      if (index > -1) {
        this.selectedRows.splice(index, 1)
      } else {
        // Only enforce selection limit when trying to add a new selection
        if (this.selectedRows.length >= this.MAX_SELECTION_LIMIT) {
          return
        }
        this.selectedRows.push(rowId)
      }
    },
    isCheckboxDisabled(item) {
      // Disable if email has been sent OR if we've reached the limit and item is not selected
      return (
        item.emailSent ||
        (this.selectedRows.length >= this.MAX_SELECTION_LIMIT &&
          !this.selectedRows.includes(item.id))
      )
    },
    formatScholasticYear(year) {
      // Format scholastic year to ensure consistency
      return year === 'K' ? 'Kindergarten' : `Year ${year}`
    },
    getDefaultStartYear() {
      // Returns current year + 2
      return new Date().getFullYear() + 2
    },
    toCapitalCase(text) {
      // Convert text to capital case (Title Case)
      if (!text) return ''
      return text
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    updateSortBy(value) {
      this.sortBy = value
    },
    updateSortDesc(value) {
      this.sortDesc = value
    },
    sortData(data) {
      // Create a copy to avoid mutating the original
      const sortedData = [...data]

      if (!this.sortBy) {
        return sortedData
      }

      sortedData.sort((a, b) => {
        let aValue = a[this.sortBy]
        let bValue = b[this.sortBy]

        // Handle date fields
        if (this.sortBy === 'received' || this.sortBy === 'studentDOB') {
          aValue = new Date(aValue)
          bValue = new Date(bValue)
        }

        // Handle numeric fields
        if (this.sortBy === 'startYear') {
          aValue = Number(aValue)
          bValue = Number(bValue)
        }

        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0
        if (aValue == null) return 1
        if (bValue == null) return -1

        // Compare values
        let comparison = 0
        if (aValue < bValue) {
          comparison = -1
        } else if (aValue > bValue) {
          comparison = 1
        }

        // Apply sort direction
        return this.sortDesc ? -comparison : comparison
      })

      return sortedData
    },
    openEditNotesModal(item) {
      this.notesData = item
      this.isEditingNote = !!item.notes
      this.notesText = item.notes || ''
      this.showNotesModal = true
    },
    closeNotesModal() {
      this.showNotesModal = false
      this.notesText = ''
      this.isEditingNote = false
    },
    saveNotes() {
      if (this.notesData) {
        // dispatch action to update notes (handles API call and state update)
        this.$store.dispatch('futureEnrolment/updateNotesForApplication', {
          schoolCode: this.schoolCode,
          applicationId: this.notesData.id,
          notes: this.notesText || null
        })
      }
      this.closeNotesModal()
    },
    deleteNotes() {
      // Store the application ID before closing the modal

      this.deleteApplicationId = this.notesData?.id || null
      this.studentNameForDelete = this.notesData?.studentName || ''
      // close notes modal
      this.closeNotesModal()
      // Show delete confirmation modal
      this.showDeleteConfirmModal = true
    },
    confirmDelete() {
      if (this.deleteApplicationId) {
        // commit mutation for immediate UI update
        this.$store.dispatch('futureEnrolment/updateNotesForApplication', {
          schoolCode: this.schoolCode,
          applicationId: this.deleteApplicationId,
          notes: ''
        })
      }
      this.closeDeleteConfirmModal()
    },
    cancelDelete() {
      this.closeDeleteConfirmModal()
    },
    closeDeleteConfirmModal() {
      this.showDeleteConfirmModal = false
      this.studentNameForDelete = ''
      this.deleteApplicationId = null
    },
    openDeleteConfirmModal(item) {
      this.registrationToDelete = item
      this.showDeleteRegistrationConfirmModal = true
    },
    async confirmDeleteRegistration() {
      if (!this.registrationToDelete) {
        return
      }

      // Call API to delete the registration of interest, which will also update the state and UI accordingly
      await this.$store.dispatch(
        'futureEnrolment/deleteRegistrationOfInterest',
        {
          schoolCode: this.schoolCode,
          applicationId: this.registrationToDelete.id
        }
      )

      this.closeDeleteRegistrationConfirmModal()
    },
    cancelDeleteRegistration() {
      this.closeDeleteRegistrationConfirmModal()
    },
    closeDeleteRegistrationConfirmModal() {
      this.showDeleteRegistrationConfirmModal = false
      this.registrationToDelete = null
    }
  }
}
</script>

<style scoped lang="scss">
.ApplicationList {
  padding: 2rem;
  margin: 0;

  .filterLabel {
    font-weight: 500;
    color: #333;
    display: inline-block;
    padding: 12px 0;
  }

  .gap-2 {
    gap: 8px;
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .note-button {
    font-size: 14px !important;
  }
}

::v-deep .v-data-footer {
  text-align: right;

  .v-search-results__row {
    background-color: #f5f5f5;
    height: 68px;
    padding: 1.5rem;
  }

  .value {
    color: #1976d2;
    font-weight: bold;
  }

  .link {
    outline: none;
    text-decoration: underline;
  }
}

::v-deep .searchInput.v-text-field {
  .v-label {
    color: rgba(0, 0, 0, 0.6) !important;
    font-size: 16px !important;
    font-weight: 400 !important;
    background-color: transparent !important;
  }

  .v-input__icon.v-input__icon--clear {
    opacity: 1;
  }

  &.v-input .v-icon {
    &.v-icon--link:focus {
      border: 3px solid #002664;
      border-radius: 50%;
      color: #002664;
    }

    &:focus::after {
      opacity: 0;
    }
  }
}

.no-styling {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
}

.row-line {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 12px;
  margin-bottom: 12px !important;
  width: 100%;
}

::v-deep td.checkbox-cell {
  width: 40px !important;
  min-width: 40px !important;
  max-width: 40px !important;
  padding: 0 !important;
  text-align: center;
  vertical-align: top !important;
}

::v-deep .checkbox-cell .v-input {
  margin-top: 4px !important;
  margin-left: 24px !important;
}

::v-deep tr {
  vertical-align: top;
}

::v-deep td {
  vertical-align: top;
}

::v-deep thead th {
  height: auto !important;
  padding: 12px 0px 24px 16px !important;
  margin-bottom: 12px !important;
}

::v-deep tbody tr:first-child td {
  padding-top: 24px !important;
}

::v-deep tbody tr td {
  padding-right: 0 !important;
}

::v-deep tbody tr:first-child td.checkbox-cell {
  padding-top: 18px !important;
}

::v-deep tbody > tr > td {
  font-size: 14px !important;
}

.results-text {
  color: #666;
  font-size: 14px;
  white-space: nowrap;
  align-items: center;
}

::v-deep
  .theme--light.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(
    .v-btn--outlined
  ) {
  background-color: $ads-dark-60 !important;
  color: $ads-white !important;
  border: 3px solid transparent;
  margin: 4px;
  .v-icon {
    color: $ads-white !important;
  }
}

.snackbar-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.snackbar-title {
  font-weight: 600;
  font-size: 14px;
}

.snackbar-message {
  font-size: 13px;
  line-height: 1.4;
}

.w-full {
  width: 100%;
}

.theme--light.v-icon.v-icon--link:focus {
  border: none !important;
  outline: 3px solid currentColor;
}

::v-deep .closeBtn {
  cursor: pointer;
}

::v-deep .closeBtn:focus {
  border: none !important;
  outline: 3px solid currentColor;
}
</style>
